import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { teamAPI, APIError } from "../../lib/api";
import { createTeamSchema, updateTeamSchema, addTeamMemberSchema } from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function createTeamAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId } = params;

  if (!orgId) {
    return { success: false, error: "Organization ID is required" };
  }

  const formData = await parseFormData(request);
  const result = createTeamSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const team = await teamAPI.create(orgId, {
      name: result.data.name,
      description: result.data.description,
    });
    return redirect(`/organizations/${orgId}/teams/${team.id}`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateTeamAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, teamId } = params;

  if (!orgId || !teamId) {
    return { success: false, error: "Organization ID and Team ID are required" };
  }

  const formData = await parseFormData(request);
  const result = updateTeamSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const team = await teamAPI.update(orgId, teamId, result.data);
    return { success: true, data: team };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function deleteTeamAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId, teamId } = params;

  if (!orgId || !teamId) {
    return { success: false, error: "Organization ID and Team ID are required" };
  }

  try {
    await teamAPI.delete(orgId, teamId);
    return redirect(`/organizations/${orgId}/teams`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function addTeamMemberAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId, teamId } = params;

  if (!orgId || !teamId) {
    return { success: false, error: "Organization ID and Team ID are required" };
  }

  const formData = await parseFormData(request);
  const result = addTeamMemberSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    await teamAPI.addMember(orgId, teamId, result.data);
    return redirect(`/organizations/${orgId}/teams/${teamId}`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function removeTeamMemberAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, teamId, userId } = params;

  if (!orgId || !teamId || !userId) {
    return { success: false, error: "Organization ID, Team ID, and User ID are required" };
  }

  try {
    await teamAPI.removeMember(orgId, teamId, userId);
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
